import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { AuthContext } from "./authContext";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { API_BASE_URL, API_KEY } from "../config/config";
import axios from "axios";

const ForumContext = createContext(null);

const ForumProvider = ({ children }) => {
	const { visitor } = useContext(AuthContext);
	const location = useLocation();

	const [nodesLoading, setNodesLoading] = useState(false);
	const [nodeTree, setNodeTree] = useState([]);
	const [nodes, setNodes] = useState([]);
	const [nodesLoaded, setNodesLoaded] = useState(false);

	const fetchNodeList = async () => {
		if (!visitor || nodesLoaded) return;

		if (nodesLoading) return; // Loading

		try {
			setNodesLoading(true);

			const config = {
				method: "get",
				maxBodyLength: Infinity,
				url: `${API_BASE_URL}/nodes`,
				headers: {
					"XF-Api-Key": API_KEY,
					"XF-Api-User": visitor.user_id,
				},
				withCredentials: true,
			};

			const response = await axios.request(config);
			const data = response.data;
			const nodeTree = data.tree_map;
			const nodes = data.nodes;

			const nodesArray = nodes.reduce((acc, node) => {
				acc[node.node_id] = node;
				return acc;
			}, {});

			setNodeTree(nodeTree);
			setNodes(nodesArray);
			setNodesLoaded(true);
			setNodesLoading(false);
		} catch (error) {
			console.error("Failed to fetch nodes:", error);
			setNodesLoaded(true);
			setNodesLoading(false);
		}
	};

	const [videosInitialLoaded, setVideosInitialLoaded] = useState(false);
	const [videosLoading, setVideosLoading] = useState(false);
	const [videos, setVideos] = useState([]);
	const [videosPage, setVideosPage] = useState(1);
	const [hasMoreVideos, setHasMoreVideos] = useState(true);

	const fetchVideos = async (isInitial = false) => {
		if (!visitor) return;

		if (videosLoading) return; // Loading

		if (isInitial && videosInitialLoaded) return; // Already loaded

		try {
			setVideosLoading(true);

			const apiUrl = `${API_BASE_URL}/videos?type=normal&page=${videosPage}&limit=20`;
			const config = {
				method: "get",
				maxBodyLength: Infinity,
				url: apiUrl,
				headers: {
					"XF-Api-Key": API_KEY,
					"XF-Api-User": visitor.user_id,
				},
				withCredentials: true,
			};

			const response = await axios.request(apiUrl, config);
			let newVideos = Object.values(response.data.videos);
			if (typeof newVideos === "object") {
				newVideos = Object.values(newVideos);
			}
			setVideos([...videos, ...newVideos]);

			const pagination = response.data.pagination;
			setHasMoreVideos(pagination.current_page < pagination.last_page);
			setVideosPage(videosPage + 1);
			setVideosLoading(false);
			setVideosInitialLoaded(true);
		} catch (error) {
			console.error("Failed to fetch videos:", error);

			setVideosLoading(false);
			setVideosInitialLoaded(true);
		}
	}

	const loadMoreVideos = () => {
		if (!hasMoreVideos)
			return;

		setVideosPage(videosPage + 1);
		fetchVideos(false);
	}

	const [forumId, setForumId] = useState(null);
	const [forumLoading, setForumLoading] = useState(false);
	const [forum, setForum] = useState(null);

	const fetchForum = useCallback(async () => {
		if (!visitor) return;

		if (forumLoading) return; // Loading

		if (forum && forum.node_id === forumId) return; // Already loaded

		try {
			setForumLoading(true);

			const config = {
				method: "get",
				maxBodyLength: Infinity,
				url: `${API_BASE_URL}/forums/${forumId}`,
				headers: {
					"XF-Api-Key": API_KEY,
					"XF-Api-User": visitor.user_id,
				},
				withCredentials: true,
			};

			const response = await axios.request(config);
			const forum = response.data.forum;

			setForum(forum);
		} catch (error) {
			console.error("Failed to fetch forum:", error);
			setForum(null);
		}
	}, [forum, forumId, forumLoading, visitor]);

	const [premiumThreadsData, setPremiumThreadsData] = useState({
		slide_premium: { threads: [] },
		slide: { threads: [] },
	});

	const [stickyThreads, setStickyThreads] = useState([]);
	const [threads, setThreads] = useState([]);
	const [threadsInitialLoaded, setThreadsInitialLoaded] = useState(false);
	const [threadsLoading, setThreadsLoading] = useState(false);
	const [threadsPage, setThreadsPage] = useState(1);
	const [hasMoreThreads, setHasMoreThreads] = useState(true);
	const [pagination, setPagination] = useState({
		current_page: 1,
		last_page: 1,
		per_page: 20,
		shown: 0,
		total: 0,
	});

	const fetchThreads = useCallback(async (isInitial = false, page = 1) => {
		if (!visitor || (isInitial && threadsInitialLoaded)) return;

		if (!forum) return;

		if (threadsLoading) return; // Loading

		if (page === pagination.current_page && !isInitial) return; // Already loaded

		try {
			setThreadsLoading(true);

			const config = {
				method: "get",
				maxBodyLength: Infinity,
				url: `${API_BASE_URL}/forums/${forumId}/threads?page=${page}&order=last_post_date`,
				headers: {
					"XF-Api-Key": API_KEY,
					"XF-Api-User": visitor.user_id,
				},
				withCredentials: true,
			};

			const response = await axios.request(config);

			if (page === 1)
			{
				let stickyThreads = response.data.sticky;
				if (typeof stickyThreads === "object") {
					stickyThreads = Object.values(stickyThreads);
				}

				setStickyThreads(stickyThreads);

				let slidePremiumThreads = response.data.slide_premium;
				if (typeof slidePremiumThreads === "object") {
					slidePremiumThreads = Object.values(slidePremiumThreads);
				}

				let slideThreads = response.data.slide;
				if (typeof slideThreads === "object") {
					slideThreads = Object.values(slideThreads);
				}

				setPremiumThreadsData({
					slide_premium: {threads: slidePremiumThreads},
					slide: {threads: slideThreads},
				});
			}

			let newThreads = response.data.threads;
			if (typeof newThreads === "object") {
				newThreads = Object.values(newThreads);
			}

			setThreads(newThreads);

			const pagination = response.data.pagination;
			setPagination(pagination);

			setHasMoreThreads(pagination.current_page < pagination.last_page);
			setThreadsLoading(false);
			setThreadsInitialLoaded(true);
		} catch (error) {
			console.error("Failed to fetch threads:", error);
			setThreadsLoading(false);
			setThreadsInitialLoaded(true);
		}
	}, [visitor, threadsInitialLoaded, forum, threadsLoading, pagination.current_page, forumId]);

	const handlePageChange = (data) => {
		if (visitor && forumId) {
			const selectedPage = data.selected + 1;
			if (selectedPage !== threadsPage && Number.isInteger(selectedPage)) {
				window.location.replace(`${forum.view_url}#page-${selectedPage}`);
				window.location.reload();
			}
		}
	};

	const {forumTitleId} = useParams();

	useEffect(() => {
		if (visitor && forumTitleId) {
			const urlPattern = /^([\w-]+)\.(\d+)$/;
			const match = forumTitleId.match(urlPattern);
			if (match) {
				const [, , matchForumId] = match;
				setForumId(Number(matchForumId));
			}
		}

		if (visitor && forumId) {
			fetchForum(forumId).then(r => {
				const hash = location.hash;
				const pageMatch = hash.match(/#page-(\d+)/);
				const page = pageMatch ? parseInt(pageMatch[1], 10) : 1;

				setThreadsPage(page);
				fetchThreads(true, page);
			});
		}
	}, [fetchForum, fetchThreads, forum, forumId, forumTitleId, location.hash, visitor]);

	return (
		<ForumContext.Provider
			value={{
				nodeTree, nodes, fetchNodeList,
				forum, fetchForum,
				...premiumThreadsData,
				stickyThreads,
				threads, threadsLoading, hasMoreThreads, threadsPage, fetchThreads, pagination, handlePageChange,
				videos, videosLoading, hasMoreVideos, videosPage, fetchVideos, loadMoreVideos
			}}
		>
			{children}
		</ForumContext.Provider>
	);
};

export { ForumContext, ForumProvider };